import React, { forwardRef } from 'react';
import { tr } from '../../../i18n/i18n';
import { ModalHandle, PrimaryModal } from '../';
import { AppButton } from '../../atoms';
import { SEVERITY_STYLES, SeverityLevel } from '../../../constant/main.constant';

interface ConfirmationModalProps {
  title?: string;
  content?: React.ReactNode;
  yesLabel?: string;
  noLabel?: string;
  icon?: string; // Optional icon class (e.g., 'pi-info-circle', 'pi-trash') - overrides default severity icon
  width?: string | number; // Optional width (e.g., '30rem' or 600)
  severity?: SeverityLevel; // Severity level for styling (uses centralized SEVERITY_STYLES)
  buttonSeverity?: 'secondary' | 'danger'; // Button severity for Yes button
  yesDisabled?: boolean; // Disable the Yes button (useful when an input is required)
  yesLoading?: boolean; // Show a spinner on the Yes button (async confirm actions)
  // Auto-focus the Yes button on open (per-caller opt-in). Defaults off, so
  // every existing confirmation keeps its current (no auto-focus) behavior.
  autoFocusYes?: boolean;
  onYes: () => void;
  onNo: () => void;
}

const ConfirmationModal = forwardRef<ModalHandle, ConfirmationModalProps>(
  (
    {
      title = tr('g.confirm_action', 'Confirm Action'),
      content = tr('g.are_you_sure_you_want_to_proceed', 'Are you sure you want to proceed?'),
      yesLabel = tr('common.button_yes', 'Yes'),
      noLabel = tr('common.button_no', 'No'),
      icon, // If not provided, uses severity default icon
      width = '500px', // Default width
      severity = 'primary', // Default to primary
      buttonSeverity, // Default undefined (no severity applied)
      onYes,
      onNo,
      yesDisabled = false,
      yesLoading = false,
      autoFocusYes = false,
    },
    ref,
  ) => {
    // Get severity-based styling from centralized configuration
    const styles = SEVERITY_STYLES[severity];

    // Use provided icon or fallback to severity default
    const displayIcon = icon || styles.icon;

    return (
      <PrimaryModal
        ref={ref}
        width={width}
        showHeader={false}
        content={
          <div className="flex flex-col items-center justify-center gap-4 p-4">
            {/* Icon with Severity-Based Styling */}
            <div
              className={`p-3 rounded-full flex items-center justify-center shadow-lg border-2 aspect-square ${styles.bgColor} ${styles.iconBorder}`}
            >
              <i className={`pi ${displayIcon} text-3xl ${styles.iconColor}`}></i>
            </div>

            {/* Title moved inside content (below icon) */}
            <h3 className="text-md font-medium text-center font-poppins">{title}</h3>

            {/* Content */}
            <div className="text-sm font-small text-center font-poppins">{content}</div>

            {/* Action Buttons — single equal-width row */}
            <div className="flex gap-2 mt-2 w-full">
              {/* Hide the secondary button when noLabel is empty (single-button modals) */}
              {noLabel ? (
                <div className="flex-1">
                  <AppButton
                    className="w-full"
                    label={noLabel}
                    outlined
                    onClick={onNo}
                    disabled={yesLoading}
                  />
                </div>
              ) : null}
              <div className="flex-1">
                <AppButton
                  className="w-full"
                  label={yesLabel}
                  onClick={onYes}
                  severity={buttonSeverity}
                  disabled={yesDisabled}
                  loading={yesLoading}
                  autoFocus={autoFocusYes}
                />
              </div>
            </div>
          </div>
        }
      />
    );
  },
);

ConfirmationModal.displayName = 'ConfirmationModal';

export default ConfirmationModal;
